import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin PIN desde variable de entorno
const ADMIN_PIN = process.env.REACT_APP_ADMIN_PIN || '2835';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [adminPinVerified, setAdminPinVerified] = useState(
    sessionStorage.getItem('adminPinVerified') === 'true'
  );

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    // Reset PIN verification on new login
    setAdminPinVerified(false);
    sessionStorage.removeItem('adminPinVerified');
    
    // Track login
    try {
      await axios.post(`${API}/access-log`, {
        page: '/login',
        page_title: 'Login',
        action: 'login',
        details: 'Usuario inició sesión'
      });
    } catch (e) {
      // Silenciar errores de tracking
    }
    
    return userData;
  };

  const verifyAdminPin = (pin) => {
    if (pin === ADMIN_PIN) {
      setAdminPinVerified(true);
      sessionStorage.setItem('adminPinVerified', 'true');
      return true;
    }
    return false;
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('adminPinVerified');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setAdminPinVerified(false);
  };

  const updateLanguage = async (language) => {
    try {
      await axios.put(`${API}/auth/language?language=${language}`);
      setUser(prev => ({ ...prev, language }));
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };

  /**
   * Hidrata la sesión con un token + user ya obtenidos (por ej. tras un
   * registro público auto-login). Equivalente a login() pero sin llamar
   * de nuevo al backend.
   */
  const setAuthData = ({ token: newToken, user: userData }) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    }
    if (userData) {
      setUser(userData);
    }
    setAdminPinVerified(false);
    sessionStorage.removeItem('adminPinVerified');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      updateLanguage,
      setAuthData,
      adminPinVerified,
      verifyAdminPin,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
